import { useState, useEffect, useRef, useCallback } from "react";
import { generateDorks } from "../utils/api";
import { exportJSON, exportCSV, exportTXT } from "../utils/export";
import { INPUT_LIMITS } from "../utils/security";

const ENGINES = [
  { id: "google",     label: "Google",     url: (q) => `https://www.google.com/search?q=${encodeURIComponent(q)}` },
  { id: "bing",       label: "Bing",        url: (q) => `https://www.bing.com/search?q=${encodeURIComponent(q)}` },
  { id: "duckduckgo", label: "DuckDuckGo",  url: (q) => `https://duckduckgo.com/?q=${encodeURIComponent(q)}` },
  { id: "brave",      label: "Brave",       url: (q) => `https://search.brave.com/search?q=${encodeURIComponent(q)}` },
];

function highlightDork(query) {
  const ops = ["site:","inurl:","intitle:","intext:","filetype:","ext:","cache:","related:","info:","allinurl:","allintitle:","allintext:","before:","after:","numrange:"];
  const parts = [];
  let remaining = query;
  while (remaining.length > 0) {
    let matched = false;
    for (const op of ops) {
      if (remaining.toLowerCase().startsWith(op.toLowerCase())) {
        const end = remaining.indexOf(" ", op.length);
        const token = end === -1 ? remaining : remaining.slice(0, end);
        parts.push(<span key={parts.length} className="op">{token}</span>);
        remaining = end === -1 ? "" : remaining.slice(end);
        matched = true; break;
      }
    }
    if (!matched) {
      if (remaining.startsWith('"')) {
        const close = remaining.indexOf('"', 1);
        if (close !== -1) {
          parts.push(<span key={parts.length} className="val">{remaining.slice(0, close + 1)}</span>);
          remaining = remaining.slice(close + 1);
          continue;
        }
      }
      const nextOp = ops.reduce((min, op) => {
        const idx = remaining.toLowerCase().indexOf(op.toLowerCase(), 1);
        return idx !== -1 && idx < min ? idx : min;
      }, remaining.length);
      const nextQuote = remaining.indexOf('"', 1);
      const cutoff = Math.min(nextOp, nextQuote === -1 ? remaining.length : nextQuote);
      parts.push(<span key={parts.length}>{remaining.slice(0, cutoff)}</span>);
      remaining = remaining.slice(cutoff);
    }
  }
  return parts;
}

// V-05: Whitelist risk values to prevent CSS class injection
const SAFE_RISKS = new Set(["high", "medium", "low"]);
function safeRiskClass(risk) {
  return SAFE_RISKS.has(risk) ? risk : "low";
}

function DorkCard({ dork, index, onAddToChain, onLoadVariation }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    // Clipboard API with execCommand fallback
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(dork.query).catch(() => legacyCopy(dork.query));
    } else {
      legacyCopy(dork.query);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={`dork-card risk-${safeRiskClass(dork.risk)}`}>
      <div className="dork-header">
        <div className="dork-query">{highlightDork(dork.query)}</div>
        <button className={`copy-btn ${copied ? "copied" : ""}`} onClick={handleCopy}>
          {copied ? "COPIED!" : "COPY"}
        </button>
      </div>
      {dork.description && <div className="dork-desc">{dork.description}</div>}
      {dork.sensitivity_note && (
        <div className="sensitivity-note">⚠ {dork.sensitivity_note}</div>
      )}
      <div className="dork-meta">
        <span className={`risk-badge ${safeRiskClass(dork.risk)}`}>
          {safeRiskClass(dork.risk).toUpperCase()}
        </span>
        {(dork.operators || []).map((op) => (
          <span key={op} className="op-tag">{op}</span>
        ))}
        <button className="chain-btn" onClick={() => onAddToChain(dork.query)}>
          [+ CHAIN]
        </button>
      </div>
      <div className="engine-row">
        {ENGINES.map((e) => (
          <a key={e.id} className="eng-btn" href={e.url(dork.query)} target="_blank" rel="noopener noreferrer">
            [{e.label}]
          </a>
        ))}
      </div>
      {dork.variations?.length > 0 && (
        <div className="variations-section">
          <div className="var-label">Variations</div>
          <div className="var-list">
            {dork.variations.map((v, i) => (
              <span
                key={i}
                className="var-chip"
                title={v}
                onClick={() => onLoadVariation(v)}
              >
                {v.length > 52 ? v.slice(0, 52) + "…" : v}
              </span>
            ))}
          </div>
        </div>
      )}
      {dork.stealth_note && (
        <div className="stealth-note">⟳ {dork.stealth_note}</div>
      )}
    </div>
  );
}

// V-12 clipboard fallback
function legacyCopy(text) {
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.style.cssText = "position:fixed;opacity:0;";
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
  } catch { /* silent */ }
}

export default function Generator({ onDorksGenerated, onAddToChain, presetQuery, isLoading, setIsLoading }) {
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState("");
  const [depth, setDepth] = useState("10");
  const [dorks, setDorks] = useState([]);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState("");
  const [opts, setOpts] = useState({ variations: true, risk: true, explain: true, stealth: false });
  const queryRef = useRef(null);
  const isGenerating = useRef(false); // V-07: prevent concurrent requests

  useEffect(() => {
    if (presetQuery) { setQuery(presetQuery); queryRef.current?.focus(); }
  }, [presetQuery]);

  const toggleOpt = (key) => setOpts((p) => ({ ...p, [key]: !p[key] }));

  const handleGenerate = async () => {
    if (!query.trim()) { queryRef.current?.focus(); return; }
    // V-07: Prevent concurrent / rapid re-submissions
    if (isGenerating.current) return;
    isGenerating.current = true;
    setIsLoading(true);
    setError("");
    setDorks([]);
    setSummary("");
    try {
      const result = await generateDorks({ query, target, depth: parseInt(depth), opts });
      setDorks(result.dorks || []);
      setSummary(result.summary || "");
      onDorksGenerated(result.dorks || []);
    } catch (err) {
      setError(err.message || "Failed to generate dorks. Check your API connection.");
    } finally {
      setIsLoading(false);
      isGenerating.current = false;
    }
  };

  const highCount = dorks.filter((d) => d.risk === "high").length;
  const medCount = dorks.filter((d) => d.risk === "medium").length;
  const opsUsed = new Set(dorks.flatMap((d) => d.operators || [])).size;

  return (
    <div>
      <div className="stealth-banner">
        ⟳ STEALTH: Dorks open in new tabs — use Tor Browser or VPN for anonymized reconnaissance
      </div>

      <div className="input-section">
        <div className="input-row">
          <div className="input-group">
            <label className="input-label">Target (optional)</label>
            <input
              className="target-input"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="example.com"
              maxLength={INPUT_LIMITS.TARGET_MAX_LENGTH}
            />
          </div>
          <div className="input-group flex-1">
            <label className="input-label">Natural Language Query</label>
            <textarea
              ref={queryRef}
              className="query-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. find exposed PDFs from university domains containing financial data..."
              maxLength={INPUT_LIMITS.QUERY_MAX_LENGTH}
              // V-07: only fire if not already generating
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !isGenerating.current) { e.preventDefault(); handleGenerate(); }}}
            />
          </div>
          <button
            className={`gen-btn ${isLoading ? "loading" : ""}`}
            onClick={handleGenerate}
            disabled={isLoading}
          >
            {isLoading ? "[ SCANNING... ]" : "[ GENERATE ]"}
          </button>
        </div>
        <div className="opts-row">
          <span className="opt-label">Options:</span>
          {Object.entries(opts).map(([k, v]) => (
            <button key={k} className={`opt-pill ${v ? "on" : ""}`} onClick={() => toggleOpt(k)}>
              {k === "variations" ? "+ Variations" : k === "risk" ? "Risk Labels" : k === "explain" ? "Explanations" : "Stealth Hints"}
            </button>
          ))}
          <select className="depth-select" value={depth} onChange={(e) => setDepth(e.target.value)}>
            {["5","10","15","20"].map((n) => <option key={n} value={n}>{n} dorks</option>)}
          </select>
        </div>
      </div>

      {error && <div className="error-msg">⚠ {error}</div>}

      {isLoading && (
        <div className="loading-skeleton">
          {[...Array(3)].map((_, i) => <div key={i} className="skel-card" style={{ animationDelay: `${i * 0.15}s` }} />)}
          <div style={{ textAlign: "center", fontSize: "10px", color: "var(--txt-muted)", marginTop: 8 }}>
            ⬡ AI analyzing intent and generating dork patterns...
          </div>
        </div>
      )}

      {!isLoading && dorks.length > 0 && (
        <>
          <div className="stats-row">
            <div className="stat-card"><div className="stat-num">{dorks.length}</div><div className="stat-lbl">Dorks Generated</div></div>
            <div className="stat-card"><div className={`stat-num ${highCount > 0 ? "red" : ""}`}>{highCount}</div><div className="stat-lbl">High Sensitivity</div></div>
            <div className="stat-card"><div className={`stat-num ${medCount > 0 ? "amber" : ""}`}>{medCount}</div><div className="stat-lbl">Medium Risk</div></div>
            <div className="stat-card"><div className="stat-num">{opsUsed}</div><div className="stat-lbl">Operators Used</div></div>
          </div>
          {summary && <div className="ai-summary"><span style={{ color: "var(--g)" }}>⬡ AI:</span> {summary}</div>}
          <div className="dork-list">
            {dorks.map((d, i) => (
              <DorkCard key={i} dork={d} index={i} onAddToChain={onAddToChain} onLoadVariation={(v) => setQuery(v)} />
            ))}
          </div>
          <div className="export-row">
            <button className="export-btn" onClick={() => exportJSON(dorks)}>⬡ Export JSON</button>
            <button className="export-btn" onClick={() => exportCSV(dorks)}>⬡ Export CSV</button>
            <button className="export-btn" onClick={() => exportTXT(dorks)}>⬡ Export TXT</button>
            <span className="export-info">{dorks.length} queries — click engine buttons to open search</span>
          </div>
        </>
      )}

      {!isLoading && dorks.length === 0 && !error && (
        <div className="empty-state">
          <div className="empty-logo">⬡ RECON DORK</div>
          <div className="empty-hint">
            Enter a natural language query above — e.g.<br />
            <span>"find exposed PDFs from university domains"</span><br />
            <span>"admin login panels on .gov sites"</span><br />
            <span>"GitHub repos with exposed API keys for target company"</span><br /><br />
            Or click a <span>preset</span> in the sidebar to start quickly.
          </div>
        </div>
      )}
    </div>
  );
}
